import { LoadFunction } from 'ol/Image';
import ImageLayer from 'ol/layer/Image';
import ImageWMS from 'ol/source/ImageWMS';
import { useCallback, useEffect, useRef, useState } from 'react';
import Map from 'ol/Map';
import { useElement } from './useElement';
import { useLayers } from './layerManagement/LayerContext';
import {
  extractTargetInfos,
  getTargetInfoByMMSI,
  InvalidMMSIError,
  useSession,
} from '@ais-ui/websockets';
import { useSelectionState } from '../../../../apps/ais/src/components/SelectionStateProvider';
import {
  useViewMode,
  ViewMode,
} from '../../../../apps/ais/src/components/ViewModeProvider';

interface ImageWMSLayerProps {
  map: Map;
  overlay: string | null;
  hasViewModeChanged: boolean;
  setHasViewModeChanged: React.Dispatch<React.SetStateAction<boolean>>;
  name: string;
}

const useUpdateTargetDetails = () => {
  const { viewMode } = useViewMode();
  const { olSession, replaySession } = useSession();
  const { targetInfoDetailsList, setTargetInfoDetailsList } =
    useSelectionState();

  const updateSelectedTargets = useCallback(async () => {
    if (targetInfoDetailsList.length === 0) return;

    const updatedList = [...targetInfoDetailsList];

    for (let index = 0; index < targetInfoDetailsList.length; index++) {
      const targetDetails = targetInfoDetailsList[index];

      if (targetDetails.extractedTargetInfo.length === 0) continue;

      const target = targetDetails.extractedTargetInfo[0];
      if ('mmsi' in target) {
        const session =
          viewMode === ViewMode.Replay ? replaySession : olSession;

        if (!session) {
          continue;
        }

        try {
          const targetInfo = await getTargetInfoByMMSI(
            session,
            Number(target.mmsi)
          );

          const extractedTargetInfo = extractTargetInfos(targetInfo);
          updatedList[index] = extractedTargetInfo;
        } catch (error) {
          if (error instanceof InvalidMMSIError) {
            console.warn(`Invalid MMSI for target at index ${index}:`, error);
            // Keep the existing target info if MMSI is invalid
            continue;
          }
          throw error;
        }
      }
    }
    setTargetInfoDetailsList(updatedList);
  }, [
    targetInfoDetailsList,
    setTargetInfoDetailsList,
    viewMode,
    olSession,
    replaySession,
  ]);

  return updateSelectedTargets;
};

export function ImageWMSLayer({
  map,
  overlay,
  hasViewModeChanged,
  setHasViewModeChanged,
  name,
}: ImageWMSLayerProps) {
  const elementRef = useRef(
    new ImageLayer<ImageWMS>({
      source: new ImageWMS({ url: '', ratio: 1 }),
    })
  );
  const prevOverlay = useRef('');
  const { removeLayer, canvasRef } = useLayers();
  const updateTargetDetails = useUpdateTargetDetails();

  useEffect(() => {
    if (hasViewModeChanged && elementRef.current) {
      removeLayer(elementRef.current);

      elementRef.current = new ImageLayer<ImageWMS>({
        source: new ImageWMS({ url: '', ratio: 1 }),
      });

      setHasViewModeChanged(false);
    }
  }, [hasViewModeChanged, setHasViewModeChanged, removeLayer]);

  const imageLoadFunction = useCallback<LoadFunction>(
    (imageWrapper) => {
      if (overlay && prevOverlay.current !== overlay) {
        prevOverlay.current = overlay;
        updateTargetDetails();
        const imageElement = imageWrapper.getImage() as HTMLImageElement;
        imageElement.src = `data:image/png;base64,${overlay}`;
        imageElement.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d', { willReadFrequently: true });

          if (ctx) {
            canvas.width = imageElement.width;
            canvas.height = imageElement.height;
            ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);
            canvasRef.current = canvas;
          }
        };
      }
    },
    [overlay, canvasRef]
  );

  useElement(elementRef.current, name);

  useEffect(() => {
    if (elementRef.current) {
      elementRef.current.getSource()?.setImageLoadFunction(imageLoadFunction);
    }
  }, [map, imageLoadFunction]);

  return null;
}
